import { useEffect, useMemo, useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import api from "@/lib/api";
import { formatRupees, todayISO } from "@/lib/format";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { toast } from "sonner";
import { ArrowLeft, ArrowDownLeft, ArrowUpRight, Save, Users, Percent } from "lucide-react";

/**
 * Full-page cash entry form.
 * kind = "in"  → Receipt (money in, green)
 * kind = "out" → Payment (money out, red)
 *
 * ERP rules:
 *  • Project (investment) is MANDATORY.
 *  • Partner tagging is MANDATORY when category name matches
 *    capital/partner/share/profit/distribution/drawings/equity keywords.
 *  • If a Loan is chosen, the amount can be split into Principal + Interest;
 *    both parts sync to the loan interest report.
 */
export default function CashEntry({ direction }) {
  const nav = useNavigate();
  const isReceipt = direction === "in";
  const [accounts, setAccounts] = useState([]);
  const [categories, setCategories] = useState([]);
  const [subs, setSubs] = useState([]);
  const [investments, setInvestments] = useState([]);
  const [partners, setPartners] = useState([]);
  const [form, setForm] = useState({
    date: todayISO(), direction, amount: "",
    account_id: "", category_id: "", sub_category_id: "", investment_id: "",
    partner_id: "", loan_id: "", principal_amount: "", interest_amount: "",
    counterparty: "", reference: "", note: "",
  });
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    Promise.all([
      api.get("/accounts"),
      api.get("/categories"),
      api.get("/subcategories"),
      api.get("/investments"),
      api.get("/partners"),
    ]).then(([a, c, s, i, p]) => {
      setAccounts(a.data); setCategories(c.data); setSubs(s.data);
      setInvestments(i.data); setPartners(p.data);
    });
  }, []);

  const catsFiltered = categories.filter((c) =>
    isReceipt ? c.kind !== "expense" : c.kind !== "income"
  );
  const subsFiltered = subs.filter((s) => s.category_id === form.category_id);
  const loans = investments.filter((i) => i.type === "loan" || i.type === "loan_taken");
  const selectedCategory = categories.find((c) => c.id === form.category_id);

  // Determine if partner tagging is required (share / capital / profit / etc.)
  const needsPartner = useMemo(() => {
    if (!selectedCategory) return false;
    const name = (selectedCategory.name || "").toLowerCase();
    return ["capital", "partner", "share", "profit", "distribution", "drawings", "equity"]
      .some((k) => name.includes(k));
  }, [selectedCategory]);

  // Auto-select loan if project itself is a loan
  useEffect(() => {
    const inv = investments.find((i) => i.id === form.investment_id);
    if (inv && (inv.type === "loan" || inv.type === "loan_taken")) {
      setForm((f) => ({ ...f, loan_id: inv.id }));
    } else {
      setForm((f) => ({ ...f, loan_id: "", principal_amount: "", interest_amount: "" }));
    }
  }, [form.investment_id, investments]);

  const save = async () => {
    if (!form.investment_id) return toast.error("Project is required for every cash entry");
    if (!form.account_id) return toast.error("Choose an account");
    if (!form.amount || Number(form.amount) <= 0) return toast.error("Enter a valid amount");
    if (needsPartner && !form.partner_id) {
      return toast.error("Partner tagging is required for share / capital / profit categories");
    }
    // If loan split provided, they must sum to amount
    const p = Number(form.principal_amount || 0);
    const i = Number(form.interest_amount || 0);
    if (form.loan_id && (p || i) && Math.abs((p + i) - Number(form.amount)) > 0.5) {
      return toast.error("Principal + Interest must equal the total amount");
    }
    setBusy(true);
    try {
      await api.post("/book/entries", {
        ...form,
        amount: Number(form.amount),
        category_id: form.category_id || null,
        sub_category_id: form.sub_category_id || null,
        investment_id: form.investment_id,
        partner_id: form.partner_id || null,
        loan_id: form.loan_id || null,
        principal_amount: p,
        interest_amount: i,
      });
      toast.success(`${isReceipt ? "Receipt" : "Payment"} saved`);
      nav("/cash-book");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed");
    } finally { setBusy(false); }
  };

  const accent = isReceipt ? "text-green-700" : "text-red-600";
  const btnCls = isReceipt
    ? "bg-green-700 hover:bg-green-800 text-white"
    : "bg-red-600 hover:bg-red-700 text-white";
  const Icon = isReceipt ? ArrowDownLeft : ArrowUpRight;

  return (
    <div className="max-w-3xl mx-auto space-y-6">
      <Link to="/cash-book" className="inline-flex items-center gap-2 text-[12px] text-slate-500 hover:text-slate-900" data-testid="cash-entry-back">
        <ArrowLeft className="h-3.5 w-3.5" /> Back to Cash & Bank
      </Link>

      <div>
        <div className="eyebrow flex items-center gap-1.5">
          <Icon className={`h-3.5 w-3.5 ${accent}`} />
          {isReceipt ? "MONEY IN" : "MONEY OUT"}
        </div>
        <h1 className="mt-2 font-editorial font-light text-[38px] leading-[1.02] sm:text-5xl md:text-6xl text-slate-900 tracking-[-0.035em]" data-testid="cash-entry-title">
          Record a <em className="italic">{isReceipt ? "receipt" : "payment"}.</em>
        </h1>
        <p className="mt-2 text-sm text-slate-500 max-w-lg">
          {isReceipt
            ? "Cash coming in — interest, dividend, cafe share, sale proceeds, capital contribution."
            : "Cash going out — bill payment, vendor payout, expense, loan interest paid, capital pulled."}
        </p>
      </div>

      <div className="border border-slate-200 bg-white rounded-md p-6 space-y-5">
        {/* Project always first */}
        <div className="border-b border-slate-100 pb-5">
          <Label>Project <span className="text-red-600">*</span></Label>
          <Select value={form.investment_id || ""} onValueChange={(v) => setForm({ ...form, investment_id: v })}>
            <SelectTrigger className="mt-1.5" data-testid="cash-project"><SelectValue placeholder="Every cash entry must be tagged to a project" /></SelectTrigger>
            <SelectContent>
              {investments.map((i) => (
                <SelectItem key={i.id} value={i.id}>
                  {i.name} <span className="text-slate-400">· {i.type}</span>
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>

        <div className="grid sm:grid-cols-2 gap-4">
          <div>
            <Label>Date</Label>
            <Input type="date" value={form.date} onChange={(e) => setForm({ ...form, date: e.target.value })} className="mt-1.5" data-testid="cash-date" />
          </div>
          <div>
            <Label>Amount (Rs)</Label>
            <Input type="number" value={form.amount} onChange={(e) => setForm({ ...form, amount: e.target.value })} placeholder="0" className={`mt-1.5 font-mono-fin text-right text-lg ${accent}`} data-testid="cash-amount" />
          </div>
        </div>

        <div className="grid sm:grid-cols-2 gap-4">
          <div>
            <Label>Account <span className="text-red-600">*</span></Label>
            <Select value={form.account_id} onValueChange={(v) => setForm({ ...form, account_id: v })}>
              <SelectTrigger className="mt-1.5" data-testid="cash-account"><SelectValue placeholder="Pick account" /></SelectTrigger>
              <SelectContent>
                {accounts.map((a) => (
                  <SelectItem key={a.id} value={a.id}>{a.name} <span className="text-slate-400">· {a.type}</span></SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label>Counterparty</Label>
            <Input value={form.counterparty} onChange={(e) => setForm({ ...form, counterparty: e.target.value })} placeholder={isReceipt ? "Who paid you?" : "Whom did you pay?"} className="mt-1.5" data-testid="cash-counterparty" />
          </div>
        </div>

        <div className="grid sm:grid-cols-2 gap-4">
          <div>
            <Label>Category</Label>
            <Select value={form.category_id || "none"} onValueChange={(v) => setForm({ ...form, category_id: v === "none" ? "" : v, sub_category_id: "" })}>
              <SelectTrigger className="mt-1.5" data-testid="cash-category"><SelectValue placeholder="— None —" /></SelectTrigger>
              <SelectContent>
                <SelectItem value="none">— None —</SelectItem>
                {catsFiltered.map((c) => (
                  <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label>Sub-category</Label>
            <Select value={form.sub_category_id || "none"} onValueChange={(v) => setForm({ ...form, sub_category_id: v === "none" ? "" : v })} disabled={!form.category_id}>
              <SelectTrigger className="mt-1.5"><SelectValue placeholder="— None —" /></SelectTrigger>
              <SelectContent>
                <SelectItem value="none">— None —</SelectItem>
                {subsFiltered.map((s) => (
                  <SelectItem key={s.id} value={s.id}>{s.name}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        </div>

        {/* Partner tagging — appears when category is share/profit/capital related */}
        {(needsPartner || form.partner_id) && (
          <div className="border border-amber-200 bg-amber-50/60 rounded-md p-4">
            <div className="flex items-center gap-2 text-[11px] text-amber-800 mb-2">
              <Users className="h-3.5 w-3.5" />
              PARTNER TAGGING {needsPartner && <span className="text-red-600">required</span>}
            </div>
            <Select value={form.partner_id || ""} onValueChange={(v) => setForm({ ...form, partner_id: v })}>
              <SelectTrigger data-testid="cash-partner"><SelectValue placeholder="Choose the partner this entry belongs to" /></SelectTrigger>
              <SelectContent>
                {partners.map((p) => (
                  <SelectItem key={p.id} value={p.id}>
                    {p.name} <span className="text-slate-400">· {p.ownership_percent}%</span>
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            <div className="text-[10px] text-amber-700 mt-2">
              This will post to the partner&apos;s ledger — you can see the balance in Partners.
            </div>
          </div>
        )}

        {/* Loan link — appears when a loan investment is chosen, or manually picked */}
        {loans.length > 0 && (
          <div className="border border-slate-200 rounded-md p-4 bg-slate-50/50">
            <div className="flex items-center gap-2 text-[11px] text-slate-600 mb-2">
              <Percent className="h-3.5 w-3.5" />
              LOAN ADJUSTMENT (optional — split principal & interest)
            </div>
            <Select value={form.loan_id || "none"} onValueChange={(v) => setForm({ ...form, loan_id: v === "none" ? "" : v })}>
              <SelectTrigger data-testid="cash-loan"><SelectValue placeholder="— Not a loan entry —" /></SelectTrigger>
              <SelectContent>
                <SelectItem value="none">— Not a loan entry —</SelectItem>
                {loans.map((l) => (
                  <SelectItem key={l.id} value={l.id}>
                    {l.name} <span className="text-slate-400">· {l.type === "loan_taken" ? "taken" : "given"}</span>
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            {form.loan_id && (
              <div className="grid grid-cols-2 gap-3 mt-3">
                <div>
                  <Label className="text-[11px]">Principal (Rs)</Label>
                  <Input type="number" value={form.principal_amount}
                    onChange={(e) => setForm({ ...form, principal_amount: e.target.value })}
                    placeholder="0" className="mt-1 font-mono-fin text-right h-9" data-testid="cash-principal" />
                </div>
                <div>
                  <Label className="text-[11px]">Interest (Rs)</Label>
                  <Input type="number" value={form.interest_amount}
                    onChange={(e) => setForm({ ...form, interest_amount: e.target.value })}
                    placeholder="0" className="mt-1 font-mono-fin text-right h-9" data-testid="cash-interest" />
                </div>
              </div>
            )}
            {form.loan_id && form.amount && (
              (Number(form.principal_amount || 0) + Number(form.interest_amount || 0)) > 0 &&
              Math.abs((Number(form.principal_amount || 0) + Number(form.interest_amount || 0)) - Number(form.amount)) > 0.5
            ) && (
              <div className="text-[10px] text-red-600 mt-2">
                Principal + Interest must equal the total amount ({formatRupees(Number(form.amount))}).
              </div>
            )}
          </div>
        )}

        <div>
          <Label>Reference (cheque / UPI / txn id)</Label>
          <Input value={form.reference} onChange={(e) => setForm({ ...form, reference: e.target.value })} className="mt-1.5" />
        </div>

        <div>
          <Label>Note</Label>
          <Textarea rows={3} value={form.note} onChange={(e) => setForm({ ...form, note: e.target.value })} placeholder="Any context or narration for this entry" className="mt-1.5" />
        </div>

        {form.amount && Number(form.amount) > 0 && (
          <div className={`p-3 border text-sm ${isReceipt ? "bg-green-50 border-green-100 text-green-900" : "bg-red-50 border-red-100 text-red-900"}`}>
            {isReceipt ? "You will receive" : "You will pay out"}: <span className="font-mono-fin font-semibold">{formatRupees(Number(form.amount))}</span>
          </div>
        )}

        <div className="flex items-center justify-end gap-2 pt-2 border-t border-slate-100">
          <Button variant="outline" onClick={() => nav("/cash-book")} className="rounded-md h-10">Cancel</Button>
          <Button onClick={save} disabled={busy} className={`rounded-md h-10 gap-2 ${btnCls}`} data-testid="cash-save">
            <Save className="h-4 w-4" /> {busy ? "Saving…" : `Save ${isReceipt ? "receipt" : "payment"}`}
          </Button>
        </div>
      </div>
    </div>
  );
}
